import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { clearAppointmentSearch, searchAppointments } from '../../../store/calendarService';
import { loadAppointmentTypeDetails } from '../../../store/directusService';
import { searchParticipants } from '../../../store/participantService';
import { loadUsersMinimalDetails } from '../../../store/userService';
import { calcStartDate, countDays, todayDate } from '../../../utils/dateFunctions';
import {
    APPOINTMENT_TYPES_ERROR,
    REFERRALS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../utils/sortFunctions';
import { hasRole, PRAP, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import CreateReferralCsv from './CreateReferralCsv';
import ReferralsCreatedTableRow from './ReferralsCreatedTableRow';

import app from '../../../app.module.css';

const headCells = [
    { id: 'ptCode', label: 'Participant ID' },
    { id: 'poNumber', label: 'PO Number' },
    { id: 'eligibilityGroupName', label: 'Eligibility Group' },
    { id: 'status', label: 'Status' },
    { id: 'statusReason', label: 'Status Reason' },
    { id: 'createdBy', label: 'Created By' },
    { id: 'referralDate', label: 'Referral Date' },
    { id: 'dateCreated', label: 'Participant Created Date' },
    {
        id: 'daysBetweenReferralAndCreated',
        label: 'Days between referral date and created date'
    },
    { id: 'initialAppointmentDate', label: 'Initial Appointment Date' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'ptCode',
    page: 0,
    rowsPerPage: 100
};

const generateRoles = [PRAP, QUALITY, SUPERUSER];
const _SEARCH_DAYS_BEFORE = 7;

const ReferralsCreated = () => {
    // HOOKS
    const dispatch = useDispatch();
    const { state } = useLocation();

    // LOCAL STATE
    const initialAppointmentParams = {
        typeIds: [],
        participantIds: [],
        dateFrom: ''
    };
    const initialPtSearchParams = {
        contractIds: [],
        dateCreatedFrom: '',
        dateCreatedTo: ''
    };
    const [rows, setRows] = useState([]);
    const [participantSearchParams, setParticipantSearchParams] = useState(initialPtSearchParams);
    const [appointmentSearchParams, setAppointmentSearchParams] =
        useState(initialAppointmentParams);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [isDataSetsModalOpen, setIsDataSetsModalOpen] = useState(false);
    const [generateCSV, setGenerateCSV] = useState(false);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const appointmentSearch = useSelector(
        (state) => state.entities.calendarService.appointmentSearch
    );
    const appointmentSearchMetaData = useSelector(
        (state) => state.entities.calendarService.appointmentSearchMetaData
    );
    const loadingAppointmentSearch = useSelector(
        (state) => state.entities.calendarService.loadingAppointmentSearch
    );
    const userDetails = useSelector((state) => state.entities.userService.usersMinimalDetails);
    const loadingParticipantsSearch = useSelector(
        (state) => state.entities.participantService.loadingParticipantsSearch
    );
    const participantsSearch = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );
    const appointmentTypeDetails = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );

    // USE EFFECTS
    useEffect(
        () => () => {
            cleanUp();
        },
        []
    );

    useEffect(() => {
        if (!state) return;
        const newPtSearchParams = {
            contractIds: [state.selects.contract.id],
            dateCreatedFrom: calcStartDate(_SEARCH_DAYS_BEFORE, todayDate()),
            dateCreatedTo: todayDate()
        };
        dispatch(searchParticipants(newPtSearchParams, 0));
        setParticipantSearchParams(newPtSearchParams);
        appointmentTypeDetails?.length < 1 && dispatch(loadAppointmentTypeDetails());
    }, [state]);

    useEffect(() => {
        if (
            participantSearchParams.dateCreatedFrom &&
            !participantsSearchMetaData.last &&
            participantsSearch?.length > 0 &&
            !loadingParticipantsSearch &&
            'number' in participantsSearchMetaData
        ) {
            dispatch(
                searchParticipants(participantSearchParams, participantsSearchMetaData.number + 1)
            );
        } else if (participantsSearchMetaData.last && participantsSearch?.length < 1) {
            setLoadingContentMessage(REFERRALS_ERROR);
        } else if (
            participantsSearchMetaData.last &&
            participantsSearch?.length > 0 &&
            appointmentTypeDetails?.length > 0
        ) {
            const ptIds = [...new Set(participantsSearch.map((el) => el.id))];
            const initialAppointmentId =
                appointmentTypeDetails.find((el) => el.name.toLowerCase() === 'initial')?.id || '';
            if (initialAppointmentId) {
                const appointmentFilters = {
                    typeIds: [initialAppointmentId],
                    participantIds: ptIds,
                    dateFrom: participantSearchParams.dateCreatedFrom
                };
                ptIds.length && dispatch(searchAppointments(appointmentFilters, 0));
                setAppointmentSearchParams(appointmentFilters);
            }
            const userIds = [...new Set(participantsSearch.map((el) => el.createdBy))];
            userIds.length && dispatch(loadUsersMinimalDetails(userIds));
        }
    }, [
        participantsSearchMetaData,
        participantsSearch,
        appointmentTypeDetails,
        participantSearchParams
    ]);

    useEffect(() => {
        if (
            appointmentSearchParams?.participantIds?.length > 0 &&
            !appointmentSearchMetaData.last &&
            appointmentSearch?.length > 0 &&
            !loadingAppointmentSearch &&
            'number' in appointmentSearchMetaData
        ) {
            dispatch(
                searchAppointments(appointmentSearchParams, appointmentSearchMetaData.number + 1)
            );
        }
    }, [
        appointmentSearch,
        loadingAppointmentSearch,
        appointmentSearchMetaData,
        appointmentSearchParams
    ]);

    useEffect(() => {
        if (
            !state?.selects?.contract?.id ||
            userDetails?.length < 1 ||
            !appointmentSearchMetaData?.last ||
            participantsSearch?.length < 1 ||
            !participantsSearchMetaData?.last
        )
            return;
        const newRows = participantsSearch
            .map((el) => {
                const initialAppointment =
                    (appointmentSearch?.length > 0 &&
                        appointmentSearch.find((entry) => entry.participantId === el.id)) ||
                    {};
                const createdByUser = userDetails.find((entry) => entry.id === el.createdBy) || {};
                return {
                    ptCode: el?.ptCode,
                    status: el?.status || '',
                    statusReason: el?.statusReason || '',
                    eligibilityGroupName: el?.eligibilityGroup || '',
                    referralDate: el?.referralDate,
                    dateCreated: el?.dateCreated,
                    daysBetweenReferralAndCreated:
                        el?.referralDate && el?.dateCreated
                            ? Math.floor(countDays(el.referralDate, el.dateCreated))
                            : '',
                    poNumber: el?.poNumber,
                    createdBy: `${createdByUser?.firstName || ''} ${createdByUser?.lastName || ''}`,
                    initialAppointmentDate: initialAppointment?.date || '1960-01-01'
                };
            })
            .filter((el) => el.ptCode);
        setRows(newRows);
    }, [appointmentSearchMetaData, participantsSearch, participantsSearchMetaData, userDetails]);

    // HELPER FNS
    const loadingError = () => {
        if (!state) return 'No Data Set query found for Referrals';
        if (appointmentTypeDetails?.length < 1) return APPOINTMENT_TYPES_ERROR;
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el, i) => (
            <ReferralsCreatedTableRow key={`${el.ptCode}-${i}`} row={el} />
        ));

    const cleanUp = () => {
        dispatch(clearAppointmentSearch());
        setRows([]);
        setParticipantSearchParams(initialPtSearchParams);
        setAppointmentSearchParams(initialAppointmentParams);
        setRowMetaData(initialRowMetaData);
        setGenerateCSV(false);
    };

    // EVENT HANDLERS
    const onDataSetsModalClose = () => {
        setIsDataSetsModalOpen(false);
    };

    const onGenerateCsv = () => {
        setGenerateCSV(true);
        setIsDataSetsModalOpen(true);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={app.pageContainer}>
            <h1 className={app.mainHeading}>
                Data Sets Results Screen - Referrals Created - {state.selects.contract.name}
            </h1>
            {rows?.length < 1 ? (
                <LoadingSpinner content={loadingContentMessage} />
            ) : (
                <>
                    <ResultsTable
                        defaultOrderBy={'ptCode'}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={participantsSearchMetaData.totalElements}
                    />
                    <Button
                        id="generateDataSets"
                        type="button"
                        disabled={!hasRole(generateRoles, roles)}
                        content="Generate"
                        onClick={onGenerateCsv}
                    />
                </>
            )}
            {generateCSV && (
                <CreateReferralCsv
                    isOpen={isDataSetsModalOpen}
                    onClose={onDataSetsModalClose}
                    rows={rows}
                    contractName={state.selects.contract.name}
                />
            )}
        </div>
    );
};

export default ReferralsCreated;
